import React from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';

import Button from '@atlaskit/button/button';
import EmptyState from '@atlaskit/empty-state/empty-state';
import Image from '@atlaskit/image/image';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';

import PreFollowingLight from './images/PreFollowingLight.svg';
import PreFollowingDark from './images/PreFollowingDark.svg';
import PostFollowingLight from './images/PostFollowingLight.svg';
import PostFollowingDark from './images/PostFollowingDark.svg';
import { FollowingFeedEmptyStateBanner } from './FollowingFeedEmptyStateBanner.tsx';

const messages = defineMessages({
	preFollowingHeading: {
		id: 'feed.end-of-following.pre-follow.header',
		defaultMessage: 'Stay connected with your team',
		description: 'Heading when user has not followed anyone yet',
	},
	preFollowingDescription: {
		id: 'feed.end-of-following.pre-follow.description',
		defaultMessage: 'Follow people and spaces to see their latest work in your feed.',
		description: 'Description when user has not followed anyone yet',
	},
	preFollowingCta: {
		id: 'feed.end-of-following.pre-follow.cta',
		defaultMessage: 'Follow people',
		description:
			'Primary button in empty state when user has not followed anyone. Opens modal to select people and spaces.',
	},
	postFollowingHeading: {
		id: 'feed.end-of-following.post-follow.header',
		defaultMessage: "You're all caught up",
		description: 'Heading when user has followed people but reached end of feed',
	},
	postFollowingDescription: {
		id: 'feed.end-of-following.post-follow.description',
		defaultMessage: 'Check back later or follow other teammates to see new updates.',
		description: 'Description when user has followed people but reached end of feed',
	},
	postFollowingCta: {
		id: 'feed.end-of-following.post-follow.cta',
		defaultMessage: 'Edit feed',
		description:
			'The text is shown on a primary call-to-action button displayed at the end of the Following feed, prompting the user to edit their feed settings (e.g. manage followed spaces or people).',
	},
});

const endOfFeedImageStyles = css({
	maxWidth: '74px',
	maxHeight: '74px',
});

const dividerContainerStyles = css({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	paddingTop: token('space.200'),
	marginTop: token('space.200'),
});

interface EndOfFollowingFeedProps extends WrappedComponentProps {
	openActivityFilterModal?: () => void;
	hasFollowedAnyone?: boolean;
	hasResults?: boolean;
}

const EndOfFollowingFeedComponent: React.FC<EndOfFollowingFeedProps> = ({
	intl,
	openActivityFilterModal,
	hasFollowedAnyone = false,
	hasResults = true,
}) => {
	const isFollowingEmpty = !hasResults;
	const isPreFollowing = !hasFollowedAnyone && isFollowingEmpty;

	if (isFollowingEmpty && expVal('cc_mohiti_for_you_empty_states_exp', 'isEnabled', false)) {
		return <FollowingFeedEmptyStateBanner openActivityFilterModal={openActivityFilterModal} />;
	}

	const heading = isPreFollowing ? messages.preFollowingHeading : messages.postFollowingHeading;
	const description = isPreFollowing
		? messages.preFollowingDescription
		: messages.postFollowingDescription;
	const ctaText = isPreFollowing ? messages.preFollowingCta : messages.postFollowingCta;

	const actionButton = (
		<Button onClick={openActivityFilterModal}>
			<FormattedMessage {...ctaText} />
		</Button>
	);

	const emptyState = (
		<EmptyState
			header={intl.formatMessage(heading)}
			description={<FormattedMessage {...description} />}
			renderImage={() => (
				<Image
					css={endOfFeedImageStyles}
					src={isPreFollowing ? PreFollowingLight : PostFollowingLight}
					srcDark={isPreFollowing ? PreFollowingDark : PostFollowingDark}
					width="74"
					height="74"
					alt=""
				/>
			)}
			primaryAction={openActivityFilterModal ? actionButton : undefined}
		/>
	);

	return isPreFollowing ? <div css={dividerContainerStyles}>{emptyState}</div> : emptyState;
};

export const EndOfFollowingFeed: React.FC<WithIntlProps<EndOfFollowingFeedProps>> & {
	WrappedComponent: React.ComponentType<EndOfFollowingFeedProps>;
} = injectIntl(EndOfFollowingFeedComponent);
